// ==================== PAYMENT SUCCESS PAGE ====================

import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  ArrowRight,
  Sparkles,
  Calendar,
  CreditCard,
  Mail,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Logo } from '../../components/ui/Logo';
import { useAuth } from '../../contexts';
import { subscriptionService } from '../../services';
import { fadeInUp, staggerContainer } from '../../utils/animations';

interface PaymentSuccessData {
  subscription?: {
    plan?: { name: string };
    billingCycle?: string;
    totalAmount?: number;
  };
  payment?: {
    amount?: number;
  };
  plan?: { name: string };
  billingCycle?: string;
}

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [subscription, setSubscription] = useState<PaymentSuccessData | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Ref to prevent double API call in React StrictMode
  const hasProcessedRef = useRef(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const processPayment = async () => {
      // Prevent double API call in React StrictMode (development mode)
      if (hasProcessedRef.current) {
        return;
      }
      hasProcessedRef.current = true;
      
      if (!sessionId) {
        setError('Invalid payment session');
        setIsProcessing(false);
        return;
      }

      try {
        const response = await subscriptionService.handlePaymentSuccess(sessionId);
        
        if (response.success && response.data) {
          setSubscription(response.data);
          
          // Refresh user data to update subscription status in auth context
          // This ensures the app knows the subscription is now ACTIVE
          await refreshUser();
        } else {
          setError(response.message || 'Failed to process payment');
        }
      } catch (err) {
        console.error('Error processing payment:', err);
        const errorMessage = err instanceof Error ? err.message : 'An error occurred while processing your payment';
        setError(errorMessage);
      } finally {
        setIsProcessing(false);
      }
    };

    processPayment();
  }, [sessionId, refreshUser]);

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-(--color-dark-navy) flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-3 border-(--color-signal-orange) border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-white font-medium">Processing your payment...</p>
          <p className="text-sm text-(--color-muted-gray) mt-1">Please don't close this page</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-(--color-dark-navy) flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-(--color-error)/10 flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">❌</span>
          </div>
          <h1 className="text-2xl font-heading font-bold text-white mb-4">
            Payment Error
          </h1>
          <p className="text-(--color-soft-gray) mb-8">{error}</p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => navigate('/app/select-plan')}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-(--color-dark-navy)">
      {/* Header */}
      <header className="bg-(--color-midnight-navy)/95 backdrop-blur-xl border-b border-(--color-border-gray)">
        <div className="container-custom flex items-center justify-center h-18 py-3">
          <Logo size="lg" />
        </div>
      </header>

      {/* Main Content */}
      <main className="container-custom py-16">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-2xl mx-auto text-center"
        >
          {/* Success Icon */}
          <motion.div variants={fadeInUp} className="mb-8">
            <div className="relative inline-block">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
                className="w-24 h-24 rounded-full bg-linear-to-br from-(--color-success) to-emerald-600 
                           flex items-center justify-center shadow-xl shadow-(--color-success)/30"
              >
                <CheckCircle size={48} className="text-white" />
              </motion.div>
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.4 }}
                className="absolute -right-2 -top-2 w-10 h-10 rounded-full bg-(--color-signal-orange) 
                           flex items-center justify-center shadow-lg"
              >
                <Sparkles size={20} className="text-white" />
              </motion.div>
            </div>
          </motion.div>

          {/* Success Message */}
          <motion.div variants={fadeInUp}>
            <h1 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              Payment Successful!
            </h1>
            <p className="text-lg text-(--color-soft-gray) mb-8">
              Thank you for subscribing to AmplifyMsg. Your account is now active 
              and ready to use.
            </p>
          </motion.div>

          {/* Subscription Details Card */}
          {subscription && (
            <motion.div
              variants={fadeInUp}
              className="bg-(--color-navy-light) border border-(--color-border-gray) rounded-2xl p-8 mb-8 text-left"
            >
              <h2 className="text-lg font-semibold text-white mb-6">
                Subscription Details
              </h2>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-(--color-orange-glow) flex items-center justify-center">
                    <Sparkles size={18} className="text-(--color-signal-orange)" />
                  </div>
                  <div>
                    <p className="text-sm text-(--color-muted-gray)">Plan</p>
                    <p className="text-white font-medium">
                      {subscription.subscription?.plan?.name || subscription.plan?.name} Plan
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-(--color-orange-glow) flex items-center justify-center">
                    <Calendar size={18} className="text-(--color-signal-orange)" />
                  </div>
                  <div>
                    <p className="text-sm text-(--color-muted-gray)">Billing Cycle</p>
                    <p className="text-white font-medium">
                      {(subscription.subscription?.billingCycle || subscription.billingCycle) === 'YEARLY' ? 'Annual' : 'Monthly'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-(--color-orange-glow) flex items-center justify-center">
                    <CreditCard size={18} className="text-(--color-signal-orange)" />
                  </div>
                  <div>
                    <p className="text-sm text-(--color-muted-gray)">Amount Paid</p>
                    <p className="text-white font-medium">
                      ${Number(subscription.subscription?.totalAmount || subscription.payment?.amount || 0).toFixed(2)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-(--color-orange-glow) flex items-center justify-center">
                    <Mail size={18} className="text-(--color-signal-orange)" />
                  </div>
                  <div>
                    <p className="text-sm text-(--color-muted-gray)">Confirmation</p>
                    <p className="text-white font-medium">Sent to your email</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate('/app/dashboard')}
              rightIcon={<ArrowRight size={18} />}
            >
              Go to Dashboard
            </Button>
            <Link to="/app/settings/billing">
              <Button variant="secondary" size="lg">
                View Billing Details
              </Button>
            </Link>
          </motion.div>

          {/* Help Link */}
          <motion.p variants={fadeInUp} className="text-sm text-(--color-muted-gray) mt-8">
            Need help getting started?{' '}
            <a href="/docs" className="text-(--color-signal-orange) hover:underline">
              Check our documentation
            </a>
          </motion.p>
        </motion.div>
      </main>
    </div>
  );
};

export { PaymentSuccessPage };

